import { withAuthenticationRequired } from "@auth0/auth0-react";
import { getResidenceCapabilities } from "../API/deviceThresholds";
import { updateDeviceThresholds } from "../API/deviceThresholds";
import TemperatureConfigSection from "../components/TemperatureConfigSection";
import WaterConfigSection from "../components/WaterConfigSection";
import ElectricityConfigSection from "../components/ElectricityConfigSection";
import { InputAdornment, MenuItem, TextField } from "@mui/material";
import { Grid, Paper, Box, Typography, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { postData } from "../API/Post";
import updateSnackbarMessage from "../functions/updateSnackbarMessage";
import { Button } from "@mui/material";
import { Stack } from "@mui/material";
import { putData } from "../API/Put";
import fetchResidences from "../functions/fetchResidences";
import fetchScreens from "../functions/fetchScreens";
import fetchData from "../API/Fetch";
import logger from "../utils/logger";
import {
  Home,
  Inventory2,
  Tv,
  WaterDrop,
  Bolt,
  Save,
  Cancel,
  Category,
} from "@mui/icons-material";

const managedFields = [
  "yellow_water_limit_per_person",
  "yellow_elec_limit_per_person",
  "red_water_limit_per_person",
  "red_elec_limit_per_person",
  "screen_brightness",
  "uplink_rate_seconds",
  "screen_on_start",
  "screen_on_end",
  "pack_id",
];

const getDefaultValue = (field, establishment, brand) => {
  if (
    establishment &&
    establishment[field] !== null &&
    establishment[field] !== undefined &&
    establishment[field] !== ""
  ) {
    return establishment[field];
  }
  if (
    brand &&
    brand[field] !== null &&
    brand[field] !== undefined &&
    brand[field] !== ""
  ) {
    return brand[field];
  }
  return "";
};

function ResidenceUpdate() {
  const establishments = useSelector((state) => state.establishments);
  const brands = useSelector((state) => state.brands);
  const packs = useSelector((state) => state.packs);

  const userDB = useSelector((state) => state.userDB);

  const navigate = useNavigate();

  const [residence, setResidence] = useState(null);
  const [deviceCapabilities, setDeviceCapabilities] = useState(null);
  const [waterThresholds, setWaterThresholds] = useState({});
  const [electricityThresholds, setElectricityThresholds] = useState({});
  const [temperatureDevices, setTemperatureDevices] = useState([]);
  const [waterDevices, setWaterDevices] = useState([]);
  const [electricityDevices, setElectricityDevices] = useState([]);
  const [effectiveDefaults, setEffectiveDefaults] = useState({});

  // États pour la gestion des zones
  const [availableZones, setAvailableZones] = useState([]);

  const params = useParams();
  const { t } = useTranslation("common");

  const { handleSubmit, control, reset, watch } = useForm({
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      current_occupation: 2,
      capacity: 4,
      establishment: null,
      zone_id: null,
      yellow_water_limit_per_person: null,
      yellow_elec_limit_per_person: null,
      red_water_limit_per_person: null,
      red_elec_limit_per_person: null,
      screen_brightness: null,
      uplink_rate_seconds: null,
      screen_on_start: null,
      screen_on_end: null,
      pack_id: null,
    },
  });

  const selectedEstablishmentId = watch("establishment");

  useEffect(() => {
    let defaults = {};
    if (
      selectedEstablishmentId &&
      establishments.length > 0 &&
      brands.length > 0
    ) {
      const selectedEst = establishments.find(
        (e) => e.id === selectedEstablishmentId
      );
      const selectedBrand = selectedEst
        ? brands.find((b) => b.id === selectedEst.brand_id)
        : null;

      if (selectedEst) {
        managedFields.forEach((field) => {
          defaults[field] = getDefaultValue(field, selectedEst, selectedBrand);
        });
      }
    }
    setEffectiveDefaults(defaults);
  }, [selectedEstablishmentId, establishments, brands, residence]);

  const validationLabels = {
    name: {
      required: t("common.required", { name: t("common.name") }),
    },
    establishment: {
      required: t("common.required", { name: t("common.establishment") }),
    },
    current_occupation: {
      number: t("common.number_only"),
      required: t("common.required", { name: t("common.current_occupation") }),
    },
    capacity: {
      number: t("common.number_only"),
      required: t("common.required", { name: t("common.capacity") }),
    },
    yellow_water_limit_per_person: {
      number: t("common.number_only"),
    },
    yellow_elec_limit_per_person: {
      number: t("common.number_only"),
    },
    red_water_limit_per_person: {
      number: t("common.number_only"),
    },
    red_elec_limit_per_person: {
      number: t("common.number_only"),
    },
    screen_brightness: {
      number: t("common.number_only"),
      min: t("common.min") + " 0",
      max: t("common.max") + " 100",
    },
    uplink_rate_seconds: {
      number: t("common.number_only"),
    },
    screen_on_start: {
      number: t("common.number_only"),
      min: t("common.min") + " 0",
      max: t("common.max") + " 23",
    },
    screen_on_end: {
      number: t("common.number_only"),
      min: t("common.min") + " 0",
      max: t("common.max") + " 23",
    },
  };

  const onSubmit = async (data) => {
    logger.log("DEBUG - Form data being submitted:", data);

    if (waterThresholds && Object.keys(waterThresholds).length > 0) {
      try {
        for (const [deviceEui, thresholds] of Object.entries(waterThresholds)) {
          const thresholdData = [
            {
              threshold_type: "yellow",
              device_type: "water",
              threshold_value: parseFloat(thresholds.yellow),
              unit: "liters",
              alerts_email: true,
              alerts_sms: false
            },
            {
              threshold_type: "red",
              device_type: "water",
              threshold_value: parseFloat(thresholds.red),
              unit: "liters",
              alerts_email: true,
              alerts_sms: false
            }
          ];
          await updateDeviceThresholds(deviceEui, thresholdData);
        }
        logger.log("Seuils eau sauvegardés avec succès");
      } catch (error) {
        updateSnackbarMessage("Erreur lors de la sauvegarde des seuils");
        return;
      }
    }

    if (electricityThresholds && Object.keys(electricityThresholds).length > 0) {
      try {
        for (const [deviceEui, thresholds] of Object.entries(electricityThresholds)) {
          const thresholdData = [
            {
              threshold_type: "yellow",
              device_type: "electricity",
              threshold_value: parseFloat(thresholds.yellow),
              unit: "kwh",
              alerts_email: true,
              alerts_sms: false
            },
            {
              threshold_type: "red",
              device_type: "electricity",
              threshold_value: parseFloat(thresholds.red),
              unit: "kwh",
              alerts_email: true,
              alerts_sms: false
            }
          ];
          await updateDeviceThresholds(deviceEui, thresholdData);
        }
        logger.log("Seuils électricité sauvegardés avec succès");
      } catch (error) {
        updateSnackbarMessage("Erreur lors de la sauvegarde des seuils électricité");
        return;
      }
    }

    const selectedEst = establishments.find((e) => e.id === data.establishment);
    const selectedBrand = selectedEst
      ? brands.find((b) => b.id === selectedEst.brand_id)
      : null;

    let jsonData = {
      name: data.name,
      establishment_id: data.establishment,
      current_occupation: data.current_occupation,
      capacity: data.capacity,
      zone_id: data.zone_id || null,
    };

    managedFields.forEach((field) => {
      const formValue = data[field];
      const defaultValue = getDefaultValue(field, selectedEst, selectedBrand);

      if (
        (field === "uplink_rate_seconds" || field === "pack_id") &&
        userDB &&
        userDB.admin
      ) {
        const valueToSave =
          field === "pack_id" ? formValue : parseFloat(formValue);
        const defaultNum =
          field === "pack_id" ? defaultValue : parseFloat(defaultValue);

        if (
          formValue !== null &&
          formValue !== undefined &&
          formValue !== "" &&
          (field === "pack_id" || !isNaN(valueToSave)) &&
          (valueToSave !== defaultNum ||
            defaultValue === "" ||
            defaultValue === null ||
            isNaN(defaultNum))
        ) {
          jsonData[field] = valueToSave;
        }
        return;
      }

      if (
        (field === "uplink_rate_seconds" || field === "pack_id") &&
        !(userDB && userDB.admin)
      ) {
        return;
      }

      const formValueProcessed =
        formValue === "" || formValue === null || formValue === undefined
          ? null
          : formValue;
      const defaultValueProcessed =
        defaultValue === "" ||
        defaultValue === null ||
        defaultValue === undefined
          ? null
          : defaultValue;

      const isNumericField =
        field.includes("limit") ||
        field.includes("screen_") ||
        field.includes("uplink");

      let formValueNum = null;
      let defaultValueNum = null;

      if (isNumericField) {
        formValueNum =
          formValueProcessed !== null ? parseFloat(formValueProcessed) : null;
        defaultValueNum =
          defaultValueProcessed !== null
            ? parseFloat(defaultValueProcessed)
            : null;
      }

      if (formValueProcessed !== null) {
        if (isNumericField) {
          if (
            formValueNum !== null &&
            !isNaN(formValueNum) &&
            (formValueNum !== defaultValueNum || defaultValueNum === null)
          ) {
            jsonData[field] = formValueNum;
          }
        } else {
          if (
            String(formValueProcessed) !== String(defaultValueProcessed) ||
            defaultValueProcessed === null
          ) {
            jsonData[field] = formValueProcessed;
          }
        }
      }
    });

    Object.keys(jsonData).forEach(
      (key) =>
        (jsonData[key] === null || jsonData[key] === undefined) &&
        delete jsonData[key]
    );

    logger.log("DEBUG - Final data being sent to API:", jsonData);

    try {
      let response = null;

      if (residence) {
        response = await putData(`/residences/${residence.id}`, jsonData);
      } else {
        response = await postData(`/residences/`, jsonData);
      }

      if (response && (response.status === 200 || response.status === 201)) {
        updateSnackbarMessage(
          residence === null
            ? t("common.added_success", { name: t("common.residence") })
            : t("common.updated_success", { name: t("common.residence") })
        );

        fetchResidences();
        fetchScreens();
        navigate("/residences/");
      } else {
        const errorDetail = response?.data?.detail || "Unknown error";
        updateSnackbarMessage(
          residence === null
            ? t("common.add_error", { name: t("common.residence") }) +
                `: ${errorDetail}`
            : t("common.update_error", { name: t("common.residence") }) +
                `: ${errorDetail}`
        );
      }
    } catch (error) {
      const errorDetail =
        error?.response?.data?.detail || error.message || "Unknown error";
      updateSnackbarMessage(
        residence === null
          ? t("common.add_error", { name: t("common.residence") }) +
              `: ${errorDetail}`
          : t("common.update_error", { name: t("common.residence") }) +
              `: ${errorDetail}`
      );
    }
  };

  useEffect(() => {
    store.dispatch({
      type: "pageActions/update",
      payload: [],
    });

    if (!params.id) {
      store.dispatch({
        type: "pageTitle/update",
        payload: t("common.add_new", { name: t("common.residence") }),
      });
      reset({
        name: "",
        current_occupation: 2,
        capacity: 4,
        establishment: null,
        ...managedFields.reduce(
          (acc, field) => ({ ...acc, [field]: null }),
          {}
        ),
      });
      return;
    }

    store.dispatch({
      type: "pageTitle/update",
      payload: t("common.update") + " " + t("common.residence"),
    });
    fetchData(`/residences/${params.id}`)
      .then((data) => {
        if (data) {
          setResidence(data);
        } else {
          updateSnackbarMessage(
            t("common.fetch_error", { name: t("common.residence") })
          );
          navigate("/residences/");
        }
      })
      .catch((error) => {
        updateSnackbarMessage(
          t("common.fetch_error", { name: t("common.residence") }) +
            `: ${error.message}`
        );
        navigate("/residences/");
      });
  }, [params.id, t, navigate, reset]);

  useEffect(() => {
    if (residence && establishments.length > 0 && brands.length > 0) {
      const resetValues = {
        name: residence.name,
        establishment: residence.establishment_id,
        current_occupation: residence.current_occupation,
        capacity: residence.capacity,
        zone_id: residence.zone_id || null,
      };

      managedFields.forEach((field) => {
        resetValues[field] =
          residence[field] !== null && residence[field] !== undefined
            ? residence[field]
            : null;
      });

      reset(resetValues, { keepDefaultValues: false });
    }
  }, [residence, reset, establishments, brands]);

  useEffect(() => {
    const fetchCapabilities = async () => {
      if (params.id && params.id !== "new") {
        try {
          const response = await getResidenceCapabilities(params.id);
          if (response.status === "success") {
            setDeviceCapabilities(response.capabilities);
            setTemperatureDevices(response.capabilities.temperature_devices || []);
            setWaterDevices(response.capabilities.water_devices || []);
            setElectricityDevices(response.capabilities.electricity_devices || []);
          }
        } catch (error) {
          // Erreur silencieuse
        }
      }
    };
    fetchCapabilities();
  }, [params.id]);

  // Charger les zones de l'établissement quand l'établissement change
  useEffect(() => {
    const loadEstablishmentZones = async () => {
      if (selectedEstablishmentId) {
        try {
          const zones = await fetchData(`/zones/establishment/${selectedEstablishmentId}`);
          setAvailableZones(zones || []);
        } catch (error) {
          setAvailableZones([]);
        }
      } else {
        setAvailableZones([]);
      }
    };
    loadEstablishmentZones();
  }, [selectedEstablishmentId]);

  const getPlaceholder = useCallback(
    (fieldName) => {
      const value = effectiveDefaults[fieldName];
      if (value !== null && value !== undefined && value !== "") {
        let displayValue = value.toString();
        if (fieldName.includes("_water_"))
          return `Default: ${displayValue} L`;
        if (fieldName.includes("_elec_")) return `Default: ${displayValue} kWh`;
        if (fieldName.includes("screen_brightness"))
          return `Default: ${displayValue} %`;
        if (fieldName.includes("uplink_rate_seconds"))
          return `Default: ${displayValue} s`;
        if (fieldName === "pack_id") {
          const pack = packs.find((p) => p.id === value);
          return `Default: ${pack ? pack.id : value}`;
        }
        if (fieldName.includes("screen_on_"))
          return `Default: ${displayValue}h`;

        return `Default: ${displayValue}`;
      }
      return "";
    },
    [effectiveDefaults, packs]
  );

  // Style commun pour les sections
  const sectionStyle = {
    p: 3,
    mb: 2,
    borderRadius: 2,
  };

  return (
    <Grid container spacing={2}>
      {/* Section Informations générales */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #78a882` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Home sx={{ color: "#78a882" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {t("common.information")}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="name"
                    label={t("common.name")}
                    fullWidth
                    {...field}
                    error={error !== undefined}
                    helperText={error ? validationLabels.name[error.type] : ""}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="establishment"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="establishment"
                    label={t("common.establishment")}
                    fullWidth
                    {...field}
                    error={error !== undefined}
                    helperText={error ? validationLabels.establishment[error.type] : ""}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    select
                  >
                    {establishments.map((establishment) => (
                      <MenuItem key={establishment.id} value={establishment.id}>
                        {establishment.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="current_occupation"
                control={control}
                rules={{
                  required: true,
                  pattern: { value: /^\d+$/, message: t("common.number_only") },
                  min: { value: 0, message: t("common.min_value", { min: 0 }) },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="current_occupation"
                    label={t("common.current_occupation")}
                    fullWidth
                    type="number"
                    {...field}
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.current_occupation[error.type] : ""}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="capacity"
                control={control}
                rules={{
                  required: true,
                  pattern: { value: /^\d+$/, message: t("common.number_only") },
                  min: { value: 0, message: t("common.min_value", { min: 0 }) },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="capacity"
                    label={t("common.capacity")}
                    fullWidth
                    type="number"
                    {...field}
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.capacity[error.type] : ""}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Section Zone */}
      {selectedEstablishmentId && (
        <Grid item xs={12}>
          <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #c4a574` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Category sx={{ color: "#c4a574" }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Zone / Catégorie
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="zone_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id="zone_id"
                      label="Zone"
                      fullWidth
                      select
                      value={field.value === null || field.value === undefined ? "" : field.value}
                      onChange={(e) => {
                        const value = e.target.value === "" ? null : parseInt(e.target.value);
                        field.onChange(value);
                      }}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ displayEmpty: true }}
                      disabled={availableZones.length === 0}
                      helperText={availableZones.length === 0 ? "Aucune zone disponible pour cet établissement" : ""}
                    >
                      <MenuItem value="">
                        <em>Aucune zone</em>
                      </MenuItem>
                      {availableZones.map((zone) => (
                        <MenuItem key={zone.id} value={zone.id}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: zone.color || "#c4a574",
                              }}
                            />
                            {zone.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* Section Pack (admin only) */}
      {userDB && userDB.admin && (
        <Grid item xs={12}>
          <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #c4a574` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Inventory2 sx={{ color: "#c4a574" }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {t("common.pack")}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="pack_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="pack_id"
                      label={t("common.pack")}
                      fullWidth
                      select
                      value={field.value === null || field.value === undefined ? "" : String(field.value)}
                      onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      error={error !== undefined}
                      helperText={error ? error.message : ""}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="">
                        <em>Aucun pack</em>
                      </MenuItem>
                      {packs.map((pack) => (
                        <MenuItem key={pack.id} value={pack.id}>
                          {`Pack ${pack.id} - ${pack.devices ? pack.devices.length : 0} device${pack.devices && pack.devices.length !== 1 ? "s" : ""}`}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* Section Paramètres écran */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #6b9dad` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Tv sx={{ color: "#6b9dad" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {t("common.screen")} - {t("common.settings")}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="screen_brightness"
                control={control}
                rules={{
                  pattern: { value: /^\d+$/, message: t("common.number_only") },
                  min: { value: 0, message: t("common.min_value", { min: 0 }) },
                  max: { value: 100, message: t("common.max_value", { max: 100 }) },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="screen_brightness"
                    label={t("common.screen_brightness")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { step: 1 },
                    }}
                    placeholder={getPlaceholder("screen_brightness")}
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                    type="number"
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.screen_brightness[error.type] : ""}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="screen_on_start"
                control={control}
                rules={{
                  pattern: { value: /^([0-1]?[0-9]|2[0-3])$/, message: t("common.invalid_hour") },
                  min: 0,
                  max: 23,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="screen_on_start"
                    label={t("common.screen_on_start")}
                    type="number"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">h</InputAdornment>,
                      inputProps: { step: 1, min: 0, max: 23 },
                    }}
                    placeholder={getPlaceholder("screen_on_start")}
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.screen_on_start[error.type] : ""}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="screen_on_end"
                control={control}
                rules={{
                  pattern: { value: /^([0-1]?[0-9]|2[0-3])$/, message: t("common.invalid_hour") },
                  min: 0,
                  max: 23,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="screen_on_end"
                    label={t("common.screen_on_end")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">h</InputAdornment>,
                      inputProps: { step: 1, min: 0, max: 23 },
                    }}
                    placeholder={getPlaceholder("screen_on_end")}
                    type="number"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.screen_on_end[error.type] : ""}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            {userDB && userDB.admin && (
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="uplink_rate_seconds"
                  control={control}
                  rules={{
                    pattern: { value: /^\d+$/, message: t("common.number_only") },
                    min: { value: 1, message: t("common.min_value", { min: 1 }) },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="uplink_rate_seconds"
                      label={t("common.uplink_rate_seconds")}
                      fullWidth
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">{t("common.seconds")}</InputAdornment>,
                        inputProps: { step: 1, min: 1 },
                      }}
                      placeholder={getPlaceholder("uplink_rate_seconds")}
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.uplink_rate_seconds[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>

      {/* Section Limites de consommation */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #7a8a7a` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WaterDrop sx={{ color: "#6b9dad" }} />
            <Bolt sx={{ color: "#c4a574" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {t("common.show_limits")}
            </Typography>
          </Box>

          {/* Limites Eau */}
          <Box sx={{ mb: 3 }}>
            <Chip
              icon={<WaterDrop sx={{ color: "#6b9dad !important" }} />}
              label={t("common.water_meter")}
              size="small"
              sx={{
                mb: 2,
                background: alpha("#6b9dad", 0.15),
                color: "#ffffff",
                "& .MuiChip-icon": { color: "#6b9dad" },
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="yellow_water_limit_per_person"
                  control={control}
                  rules={{ pattern: { value: /^\d*\.?\d*$/, message: t("common.number_only") } }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="yellow_water_limit_per_person"
                      label={t("common.yellow_water_limit_per_person")}
                      type="number"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">L</InputAdornment>,
                        inputProps: { step: "any" },
                      }}
                      placeholder={getPlaceholder("yellow_water_limit_per_person")}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.yellow_water_limit_per_person[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="red_water_limit_per_person"
                  control={control}
                  rules={{ pattern: { value: /^\d*\.?\d*$/, message: t("common.number_only") } }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="red_water_limit_per_person"
                      label={t("common.red_water_limit_per_person")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">L</InputAdornment>,
                        inputProps: { step: "any" },
                      }}
                      placeholder={getPlaceholder("red_water_limit_per_person")}
                      type="number"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.red_water_limit_per_person[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Limites Électricité */}
          <Box>
            <Chip
              icon={<Bolt sx={{ color: "#c4a574 !important" }} />}
              label={t("common.electricity_meter")}
              size="small"
              sx={{
                mb: 2,
                background: alpha("#c4a574", 0.15),
                color: "#ffffff",
                "& .MuiChip-icon": { color: "#c4a574" },
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="yellow_elec_limit_per_person"
                  control={control}
                  rules={{ pattern: { value: /^\d*\.?\d*$/, message: t("common.number_only") } }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="yellow_elec_limit_per_person"
                      label={t("common.yellow_elec_limit_per_person")}
                      fullWidth
                      InputProps={{
                        endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
                        inputProps: { step: "any" },
                      }}
                      placeholder={getPlaceholder("yellow_elec_limit_per_person")}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                      type="number"
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.yellow_elec_limit_per_person[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="red_elec_limit_per_person"
                  control={control}
                  rules={{ pattern: { value: /^\d*\.?\d*$/, message: t("common.number_only") } }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="red_elec_limit_per_person"
                      label={t("common.red_elec_limit_per_person")}
                      fullWidth
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
                        inputProps: { step: "any" },
                      }}
                      placeholder={getPlaceholder("red_elec_limit_per_person")}
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.red_elec_limit_per_person[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      {/* Section Température */}
      {deviceCapabilities?.hasTemperature && (
        <Grid item xs={12}>
          <TemperatureConfigSection
            temperatureDevices={temperatureDevices}
            onUpdate={(message) => updateSnackbarMessage(message)}
          />
        </Grid>
      )}

      {/* Sections Eau et Électricité côte à côte */}
      {(deviceCapabilities?.hasWater || deviceCapabilities?.hasElectricity) && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {deviceCapabilities?.hasWater && (
              <Grid item xs={12} md={6}>
                <WaterConfigSection
                  waterDevices={waterDevices}
                  onUpdate={(message) => updateSnackbarMessage(message)}
                  onThresholdsChange={setWaterThresholds}
                />
              </Grid>
            )}
            {deviceCapabilities?.hasElectricity && (
              <Grid item xs={12} md={6}>
                <ElectricityConfigSection
                  electricityDevices={electricityDevices}
                  onUpdate={(message) => updateSnackbarMessage(message)}
                  onThresholdsChange={setElectricityThresholds}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}

      {/* Boutons d'action */}
      <Grid item xs={12}>
        <Stack
          spacing={2}
          direction="row"
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={() => navigate("/residences/")}
            sx={{ color: "#8a9ba8", borderColor: "#8a9ba8" }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit(onSubmit)}
            sx={{
              background: "#78a882",
              "&:hover": { background: "#6a9a74" }
            }}
          >
            {residence === null ? t("common.add") : t("common.update")}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(ResidenceUpdate);
